/**
 * Top Breakdown Chart
 * Horizontal bar chart of a top-N {name, count} breakdown.
 * Reused for top services, top providers, and top categories.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { BarChartHorizontal, Info } from 'lucide-react'
import type { BreakdownDatum } from '@/types/dashboard'
import { useLocalization } from '@/contexts/LocalizationContext'

// ==================== Types ====================

interface TopBreakdownChartProps {
  title: string
  description?: string
  data: BreakdownDatum[]
  /** Bar colour (defaults to brand dark blue). */
  color?: string
  loading?: boolean
  /** Message shown when there is no data (defaults to the generic empty state). */
  emptyMessage?: string
}

// ==================== Component ====================

export function TopBreakdownChart({
  title,
  description,
  data,
  color = '#131375',
  loading = false,
  emptyMessage,
}: TopBreakdownChartProps) {
  const { t } = useLocalization()

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-28 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  const chartData = data || []
  const hasData = chartData.some((d) => d.count > 0)

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <BarChartHorizontal className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
        {description && <CardDescription className="mt-1">{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Info className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-sm text-muted-foreground max-w-[240px]">
              {emptyMessage || t('dashboard.noDataAvailable')}
            </p>
          </div>
        ) : (
          <div className="h-64 min-h-[256px] min-w-0 w-full">
            <ResponsiveContainer width="100%" height={256}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
                <Bar dataKey="count" fill={color} radius={[0, 6, 6, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
